import { httpClient } from './http-client';
import type { ApiResponse } from './api.types';
import type { OrdenDeVenta } from '@/modelos/documentosComerciales/OrdenDeVenta';
import type { DocumentoComercialItem } from '@/modelos/documentosComerciales/DocumentoComercialItem';
import type { DocumentoRelacionadoComercialDto } from '@/modelos/documentosComerciales/DocumentoRelacionadoComercial';

export interface OrdenDeVentaItemDto {
  id: string;
  empresaId?: string;
  establecimientoId?: string;
  establecimientoCodigo?: string;
  establecimientoNombre?: string;
  productoId?: string;
  productoCodigo?: string;
  productoNombre: string;
  codigoSunat?: string;
  descripcion?: string;
  unidadMedidaId?: string;
  unidadMedidaNombre?: string;
  unidadMedidaCodigoSunat?: string;
  afectacionTributariaId?: string;
  afectacionTributariaCodigo?: string;
  afectacionTributariaCodigoTributo?: string;
  afectacionTributariaImpuesto?: string;
  afectacionTributariaNombre?: string;
  categoriaId?: string;
  categoriaNombre?: string;
  cantidad: number;
  precioUnitario: number;
  valorVentaUnitario: number;
  precioVentaUnitario: number;
  descuento: number;
  descuentoFactor: number;
  tipoDescuentoFormato?: string;
  valorVentaNeto: number;
  precioVentaNeto: number;
  valorVentaDescontado: number;
  precioVentaDescontado: number;
  subtotal: number;
  igvPorcentaje: number;
  igv: number;
  igvTotal: number;
  total: number;
  orden: number;
  esDetraccion?: boolean;
  detraccionCodigo?: string;
  detraccionPorcentaje?: number;
  usuarioId?: string;
  usuarioNombre?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface OrdenDeVentaListadoDto {
  id: string;
  empresaId?: string;
  establecimientoId?: string;
  establecimientoCodigo?: string;
  establecimientoNombre?: string;
  serie?: string;
  correlativo?: string;
  numero?: string;
  fechaEmision?: string;
  fechaEntrega?: string;
  clienteId?: string;
  clienteNumeroDocumento?: string;
  clienteTipoDocumentoCodigo?: string;
  clienteNombres?: string;
  clienteCorreo?: string;
  clienteDireccion?: string;
  vendedorId?: string;
  vendedorNombre?: string;
  monedaCodigo?: string;
  monedaNombre?: string;
  tipoCambio?: number;
  formaPagoId?: string;
  formaPagoNombre?: string;
  cotizacionOrigenId?: string;
  metodoEnvio?: string;
  fechaEnvioPrevisto?: string;
  ordenCompra?: string;
  numeroGuia?: string;
  centroCosto?: string;
  subtotal?: number;
  montoImponible?: number;
  descuentoTotal?: number;
  valorDescuentoGlobal?: number;
  tipoDescuentoGlobal?: string;
  descuentoItems?: number;
  exonerado?: number;
  inafecto?: number;
  exportacion?: number;
  igv?: number;
  total?: number;
  estado?: string;
  tieneNotaSalidaActiva?: boolean;
  motivoAnulacion?: string;
  observacion?: string;
  observacionInterno?: string;
  usuarioId?: string;
  usuarioNombre?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface OrdenVentaHistorialDto {
  id: string;
  estado: string;
  descripcion?: string;
  usuarioNombre?: string;
  createdAt: string;
}

export interface OrdenDeVentaDto extends OrdenDeVentaListadoDto {
  items: OrdenDeVentaItemDto[];
  historialDocumentos?: OrdenVentaHistorialDto[];
}

export interface OrdenDeVentaItemInputDto {
  id?: string;
  productoId?: string;
  productoCodigo?: string;
  productoNombre: string;
  codigoSunat?: string;
  descripcion?: string;
  unidadMedidaId?: string;
  unidadMedidaNombre?: string;
  unidadMedidaCodigoSunat?: string;
  afectacionTributariaId?: string;
  afectacionTributariaCodigo?: string;
  afectacionTributariaCodigoTributo?: string;
  afectacionTributariaImpuesto?: string;
  afectacionTributariaNombre?: string;
  categoriaId?: string;
  categoriaNombre?: string;
  cantidad: number;
  precioUnitario: number;
  valorVentaUnitario?: number;
  precioVentaUnitario?: number;
  descuento: number;
  descuentoFactor?: number;
  tipoDescuentoFormato?: string;
  valorVentaNeto?: number;
  precioVentaNeto?: number;
  valorVentaDescontado?: number;
  precioVentaDescontado?: number;
  subtotal: number;
  igvPorcentaje: number;
  igv: number;
  igvTotal?: number;
  total: number;
  orden: number;
}

export interface OrdenDeVentaInputDto {
  serie?: string;
  fechaEmision: string;
  fechaEntrega?: string;
  clienteId?: string;
  clienteNumeroDocumento?: string;
  clienteTipoDocumentoCodigo?: string;
  clienteNombres?: string;
  clienteCorreo?: string;
  clienteDireccion?: string;
  vendedorId?: string;
  monedaCodigo: string;
  monedaNombre?: string;
  tipoCambio: number;
  formaPagoId?: string;
  cotizacionOrigenId?: string;
  metodoEnvio?: string;
  fechaEnvioPrevisto?: string;
  ordenCompra?: string;
  numeroGuia?: string;
  centroCosto?: string;
  subtotal: number;
  montoImponible?: number;
  descuentoTotal: number;
  valorDescuentoGlobal?: number;
  tipoDescuentoGlobal?: string;
  descuentoItems?: number;
  exonerado?: number;
  inafecto?: number;
  exportacion?: number;
  igv: number;
  total: number;
  observacion?: string;
  observacionInterno?: string;
  items: OrdenDeVentaItemInputDto[];
}

function dtoAItem(dto: OrdenDeVentaItemDto): DocumentoComercialItem {
  return {
    id: dto.id,
    empresaId: dto.empresaId,
    establecimientoId: dto.establecimientoId,
    establecimientoCodigo: dto.establecimientoCodigo,
    establecimientoNombre: dto.establecimientoNombre,
    productoId: dto.productoId,
    productoCodigo: dto.productoCodigo,
    productoNombre: dto.productoNombre,
    codigoSunat: dto.codigoSunat,
    descripcion: dto.descripcion,
    unidadMedidaId: dto.unidadMedidaId,
    unidadMedidaNombre: dto.unidadMedidaNombre,
    unidadMedidaCodigoSunat: dto.unidadMedidaCodigoSunat,
    afectacionTributariaId: dto.afectacionTributariaId,
    afectacionTributariaCodigo: dto.afectacionTributariaCodigo,
    afectacionTributariaCodigoTributo: dto.afectacionTributariaCodigoTributo,
    afectacionTributariaImpuesto: dto.afectacionTributariaImpuesto,
    afectacionTributariaNombre: dto.afectacionTributariaNombre,
    categoriaId: dto.categoriaId,
    categoriaNombre: dto.categoriaNombre,
    cantidad: dto.cantidad,
    precioUnitario: dto.precioUnitario,
    valorVentaUnitario: dto.valorVentaUnitario ?? 0,
    precioVentaUnitario: dto.precioVentaUnitario ?? 0,
    descuento: dto.descuento,
    descuentoFactor: dto.descuentoFactor ?? 0,
    tipoDescuentoFormato: dto.tipoDescuentoFormato,
    valorVentaNeto: dto.valorVentaNeto ?? 0,
    precioVentaNeto: dto.precioVentaNeto ?? 0,
    valorVentaDescontado: dto.valorVentaDescontado ?? 0,
    precioVentaDescontado: dto.precioVentaDescontado ?? 0,
    subtotal: dto.subtotal,
    igvPorcentaje: dto.igvPorcentaje,
    igv: dto.igv,
    igvTotal: dto.igvTotal ?? dto.igv,
    total: dto.total,
    orden: dto.orden,
    esDetraccion: dto.esDetraccion,
    detraccionCodigo: dto.detraccionCodigo,
    detraccionPorcentaje: dto.detraccionPorcentaje,
    usuarioId: dto.usuarioId,
    usuarioNombre: dto.usuarioNombre,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

export function dtoAOrdenDeVenta(dto: OrdenDeVentaListadoDto): OrdenDeVenta {
  return {
    id: dto.id,
    empresaId: dto.empresaId ?? '',
    establecimientoId: dto.establecimientoId ?? '',
    establecimientoCodigo: dto.establecimientoCodigo,
    establecimientoNombre: dto.establecimientoNombre,
    serie: dto.serie ?? '',
    correlativo: dto.correlativo,
    numero: dto.numero,
    fechaEmision: dto.fechaEmision ?? '',
    fechaEntrega: dto.fechaEntrega,
    clienteId: dto.clienteId,
    clienteNumeroDocumento: dto.clienteNumeroDocumento,
    clienteTipoDocumentoCodigo: dto.clienteTipoDocumentoCodigo,
    clienteNombres: dto.clienteNombres,
    clienteCorreo: dto.clienteCorreo,
    clienteDireccion: dto.clienteDireccion,
    vendedorId: dto.vendedorId,
    vendedorNombre: dto.vendedorNombre,
    monedaCodigo: dto.monedaCodigo ?? 'PEN',
    monedaNombre: dto.monedaNombre,
    tipoCambio: dto.tipoCambio ?? 1,
    formaPagoId: dto.formaPagoId,
    formaPagoNombre: dto.formaPagoNombre,
    cotizacionOrigenId: dto.cotizacionOrigenId,
    metodoEnvio: dto.metodoEnvio,
    fechaEnvioPrevisto: dto.fechaEnvioPrevisto,
    ordenCompra: dto.ordenCompra,
    numeroGuia: dto.numeroGuia,
    centroCosto: dto.centroCosto,
    subtotal: dto.subtotal ?? 0,
    montoImponible: dto.montoImponible ?? 0,
    descuentoTotal: dto.descuentoTotal ?? 0,
    valorDescuentoGlobal: dto.valorDescuentoGlobal ?? 0,
    tipoDescuentoGlobal: dto.tipoDescuentoGlobal,
    descuentoItems: dto.descuentoItems ?? 0,
    exonerado: dto.exonerado ?? 0,
    inafecto: dto.inafecto ?? 0,
    exportacion: dto.exportacion ?? 0,
    igv: dto.igv ?? 0,
    total: dto.total ?? 0,
    estado: (dto.estado as OrdenDeVenta['estado']) ?? 'borrador',
    tieneNotaSalidaActiva: dto.tieneNotaSalidaActiva ?? false,
    motivoAnulacion: dto.motivoAnulacion,
    observacion: dto.observacion,
    observacionInterno: dto.observacionInterno,
    usuarioId: dto.usuarioId,
    usuarioNombre: dto.usuarioNombre,
    createdAt: dto.createdAt ?? '',
    updatedAt: dto.updatedAt ?? '',
    items: (dto as OrdenDeVentaDto).items?.map(dtoAItem),
    historial: (dto as OrdenDeVentaDto).historialDocumentos,
  };
}

export const ordenesVentaService = {
  getAll: (params?: Record<string, string | number | boolean | undefined>): Promise<ApiResponse<OrdenDeVentaListadoDto[]>> =>
    httpClient.get('/ordenes-venta', params),

  getById: async (id: string): Promise<ApiResponse<OrdenDeVenta>> => {
    const res = await httpClient.get<OrdenDeVentaDto>(`/ordenes-venta/${id}`);
    if (!res.ok || !res.data) return { ...res, data: undefined as unknown as OrdenDeVenta };
    return { ...res, data: dtoAOrdenDeVenta(res.data) };
  },

  create: (data: OrdenDeVentaInputDto): Promise<ApiResponse<OrdenDeVentaListadoDto>> =>
    httpClient.post('/ordenes-venta', data),

  update: (id: string, data: OrdenDeVentaInputDto): Promise<ApiResponse<OrdenDeVentaListadoDto>> =>
    httpClient.put(`/ordenes-venta/${id}`, data),

  generar: (id: string, serie: string): Promise<ApiResponse<OrdenDeVentaListadoDto>> =>
    httpClient.patch(`/ordenes-venta/${id}/generar`, { serie }),

  getRelacionados: (id: string): Promise<ApiResponse<DocumentoRelacionadoComercialDto[]>> =>
    httpClient.get(`/ordenes-venta/${id}/relacionados`),

  getReservasPorAlmacen: (id: string): Promise<ApiResponse<{ productoId: string; almacenId: string; cantidad: number }[]>> =>
    httpClient.get(`/ordenes-venta/${id}/reservas-por-almacen`),

  anular: (id: string, motivo: string): Promise<ApiResponse<OrdenDeVentaListadoDto>> =>
    httpClient.patch(`/ordenes-venta/${id}/anular`, { motivo }),

  eliminar: (id: string): Promise<ApiResponse<void>> =>
    httpClient.delete(`/ordenes-venta/${id}`),

  downloadPdf: (id: string): Promise<Blob> =>
    httpClient.obtenerBlob(`/ordenes-venta/${id}/pdf`),

  enviarCorreo: (id: string, email: string, verUrl: string): Promise<ApiResponse<void>> =>
    httpClient.post(`/ordenes-venta/${id}/enviar-correo`, { email, verUrl }),

  downloadExcel: (params?: {
    empresaId?: string;
    establecimientoId?: string;
    search?: string;
    estado?: string;
    fechaDesde?: string;
    fechaHasta?: string;
    vendedor?: string;
    moneda?: string;
    totalMin?: number;
    totalMax?: number;
    ids?: string[];
  }): Promise<Blob> => {
    const { ids, ...rest } = params ?? {};
    const query: Record<string, string | number | boolean | undefined> = rest as Record<string, string | number | boolean | undefined>;
    if (ids && ids.length > 0) query.ids = ids.join(',');
    return httpClient.obtenerBlob('/reportes/ordenes-venta/excel', query);
  },
};
